import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import { Session, User as SupabaseUser } from '@supabase/supabase-js'

interface User {
  id: string
  email: string
  name?: string
  avatar_url?: string
  role: 'admin' | 'editor' | 'viewer'
  metadata?: Record<string, any>
}

interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  checkAuth: () => Promise<void>
  refreshSession: () => Promise<void>
  updateUserMetadata: (metadata: Partial<User>) => void
}

/**
 * Convert Supabase user to our User interface
 */
function mapSupabaseUser(supabaseUser: SupabaseUser): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email!,
    name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0],
    avatar_url: supabaseUser.user_metadata?.avatar_url,
    // Default to admin for @mission-solar.at emails during migration
    role: supabaseUser.email?.endsWith('@mission-solar.at') ? 'admin' :
          (supabaseUser.user_metadata?.role || 'viewer'),
    metadata: supabaseUser.user_metadata
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,

  /**
   * Sign in with email and password using Supabase Auth
   * NO MORE HARDCODED PASSWORDS!
   */
  signIn: async (email: string, password: string) => {
    try {
      set({ isLoading: true })

      // Use Supabase authentication exclusively
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        console.error('[AUTH] Sign in error:', error)
        throw error
      }

      if (!data.session || !data.user) {
        throw new Error('No session received from Supabase')
      }

      // Map user and store session
      const user = mapSupabaseUser(data.user)

      set({
        user,
        session: data.session,
        isAuthenticated: true,
        isLoading: false
      })

      console.log('[AUTH] Sign in successful:', user.email)

    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  /**
   * Sign up new user
   */
  signUp: async (email: string, password: string) => {
    try {
      set({ isLoading: true })

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`
        }
      })

      if (error) {
        console.error('[AUTH] Sign up error:', error)
        throw error
      }

      if (data.user && data.session) {
        const user = mapSupabaseUser(data.user)

        set({
          user,
          session: data.session,
          isAuthenticated: true,
          isLoading: false
        })

        console.log('[AUTH] Sign up successful:', user.email)
      } else {
        // User created but needs email verification
        set({ isLoading: false })
        throw new Error('Please check your email to verify your account')
      }

    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  /**
   * Sign out and clear session
   */
  signOut: async () => {
    try {
      set({ isLoading: true })

      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error('[AUTH] Sign out error:', error)
        // Continue with local cleanup even if remote signout fails
      }

      // Clear all state
      set({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false
      })

      console.log('[AUTH] Signed out successfully')

    } catch (error) {
      console.error('[AUTH] Sign out error:', error)
      // Force clear state anyway
      set({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false
      })
    }
  },

  /**
   * Check and restore authentication state
   */
  checkAuth: async () => {
    try {
      set({ isLoading: true })

      // Get current session from Supabase
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        console.error('[AUTH] Session error:', sessionError)
        throw sessionError
      }

      if (!session) {
        // No session - user is not authenticated
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          isLoading: false
        })
        console.log('[AUTH] No session found')
        return
      }

      // Verify the session is still valid by getting user
      const { data: { user: supabaseUser }, error: userError } = await supabase.auth.getUser()

      if (userError) {
        console.error('[AUTH] User fetch error:', userError)
        // Session might be invalid
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          isLoading: false
        })
        return
      }

      if (!supabaseUser) {
        console.error('[AUTH] No user despite having session')
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          isLoading: false
        })
        return
      }

      // Valid session and user
      const user = mapSupabaseUser(supabaseUser)

      set({
        user,
        session,
        isAuthenticated: true,
        isLoading: false
      })

      console.log('[AUTH] Session restored for:', user.email)

    } catch (error) {
      console.error('[AUTH] Check auth error:', error)
      set({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false
      })
    }
  },

  /**
   * Manually refresh the session
   */
  refreshSession: async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()

      if (error) {
        console.error('[AUTH] Refresh session error:', error)
        throw error
      }

      if (!data.session) {
        throw new Error('No session returned from refresh')
      }

      const user = mapSupabaseUser(data.user!)

      set({
        user,
        session: data.session,
        isAuthenticated: true
      })

      console.log('[AUTH] Session refreshed for:', user.email)

    } catch (error) {
      console.error('[AUTH] Refresh failed:', error)
      // If refresh fails, clear auth state
      get().signOut()
      throw error
    }
  },

  /**
   * Update user metadata locally (for UI updates)
   */
  updateUserMetadata: (metadata: Partial<User>) => {
    const currentUser = get().user
    if (currentUser) {
      set({
        user: { ...currentUser, ...metadata }
      })
    }
  }
}))

/**
 * Set up auth state change listener
 * This ensures our store stays in sync with Supabase auth state
 */
supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('[AUTH] Auth state changed:', event, !!session)

  const store = useAuthStore.getState()

  switch (event) {
    case 'SIGNED_IN':
      if (session?.user) {
        const user = mapSupabaseUser(session.user)
        store.updateUserMetadata(user)
        useAuthStore.setState({
          user,
          session,
          isAuthenticated: true,
          isLoading: false
        })
      }
      break

    case 'SIGNED_OUT':
      useAuthStore.setState({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false
      })
      break

    case 'TOKEN_REFRESHED':
      if (session) {
        console.log('[AUTH] Token refreshed successfully')
        useAuthStore.setState({ session })
      }
      break

    case 'USER_UPDATED':
      if (session?.user) {
        const user = mapSupabaseUser(session.user)
        store.updateUserMetadata(user)
      }
      break

    default:
      break
  }
})